import { Route, Routes } from '@angular/router';
import { AuthenticationGuard } from '@core/authentication';
import { AdminGuard } from '@core/authentication/guards/admin/admin.guard';
import { moduleContextGuard, projectContextDeactivateGuard, projectContextGuard, systemModuleSlugGuard } from '@core/guards/current-organization-context';
import { createLegacyModuleScopedRedirectGuard, createLegacyProjectScopedRedirectGuard, legacyAskemoRedirectGuard } from '@core/guards/legacy-routes';
import { unsavedChangesGuard } from '@core/guards/unsaved-changes';
import { joinAskemoHub, leaveAskemoHub } from '@pages/askemos/services/askemo.hub.service';
import { resultsHubRoutes } from '@pages/results-hub/results-hub.routes';
import { joinContactImportHub, leaveContactImportHub } from '@pages/contacts/services/contact-import.hub.service';
import { landingPageGuard } from '@shared/components/private-pages/landing-page.guard';
import { privatePagesGuard } from '@shared/components/private-pages/private-pages.guard';
import { publicPagesGuard } from '@shared/components/public-pages/public-pages.guard';

const askemoDetailsRoutes: Routes = [
    {
        path: ':askemoId',
        canActivate: [joinAskemoHub],
        canDeactivate: [leaveAskemoHub],
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'design',
            },
            {
                path: 'design',
                title: 'str_page_titleAskemoDesign',
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () => import('@pages/askemos/askemo-design-page/askemo-design-page.component').then(c => c.AskemoDesignPageComponent),
            },
            {
                path: 'invitation',
                title: 'str_page_titleAskemoInvitation',
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () => import('@pages/askemos/askemo-invitation-page/askemo-invitation-page.component').then(c => c.AskemoInvitationPageComponent),
            },
            {
                path: 'contacts',
                title: 'str_page_titleAskemoContacts',
                loadComponent: () => import('@pages/askemos/askemo-contacts-page/askemo-contacts-page.component').then(c => c.AskemoContactsPageComponent),
            },
            {
                path: 'sessions',
                children: [
                    {
                        path: '',
                        title: 'str_page_titleAskemoSessions',
                        canDeactivate: [unsavedChangesGuard],
                        loadComponent: () => import('@pages/askemos/sessions-list-page/sessions-list-page.component').then(c => c.SessionsListPageComponent),
                    },
                    {
                        path: ':sessionId',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                title: 'str_page_titleAskemoSession',
                                canDeactivate: [unsavedChangesGuard],
                                loadComponent: () => import('@pages/askemos/askemo-session-page/askemo-session-page.component').then(c => c.AskemoSessionPageComponent),
                            },
                            {
                                path: 'messages/send',
                                title: 'str_messagePage_title',
                                canDeactivate: [unsavedChangesGuard],
                                loadComponent: () => import('@pages/messages/message-send-page/message-send-page.component').then(c => c.MessageSendPageComponent),
                            },
                        ],
                    },
                ],
            },
            {
                path: 'schedule',
                title: 'str_page_titleAskemoSendAndSummary',
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                    import('@pages/askemos/askemo-schedule-and-summary-page/askemo-schedule-and-summary-page.component').then(c => c.AskemoScheduleAndSummaryPageComponent),
            },
        ],
    },
];

const contactsRoutes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        title: 'str_page_titleContacts',
        loadComponent: () => import('@pages/contacts/components/contact-list/contact-list.component').then(c => c.ContactListComponent),
    },
    {
        path: 'import/:contactImportId',
        children: [
            {
                path: 'map',
                title: 'str_page_titleContacts',
                loadComponent: () => import('@pages/contacts/components/contact-mapping/contact-mapping.component').then(c => c.ContactMappingComponent),
            },
            {
                path: 'validate',
                title: 'str_page_titleContacts',
                loadComponent: () => import('@pages/contacts/components/contact-validation/contact-validation.component').then(c => c.ContactValidationComponent),
            },
        ],
    },
    {
        path: 'groups',
        title: 'str_page_contactGroups',
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('@pages/contacts/contact-groups/contact-group-list-page/contact-group-list-page.component').then(c => c.ContactGroupListPageComponent),
            },
            {
                path: ':contactGroupId',
                loadComponent: () =>
                    import('@pages/contacts/contact-groups/contact-group-details-page/contact-group-details-page.component').then(c => c.ContactGroupDetailsPageComponent),
            },
        ],
    },
];

const askemosRoutes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        title: 'str_page_titleAskemos',
        loadComponent: () => import('@pages/askemos/components/askemos-list/askemos-list.component').then(c => c.AskemosListComponent),
    },
    ...askemoDetailsRoutes,
];

const alertsRoutes: Routes = [
    {
        path: '',
        loadComponent: () => import('./pages/alerts/alerts-list/alerts-list-page.component').then(c => c.AlertsListComponent),
    },
    {
        path: ':alertId/logs',
        loadComponent: () => import('./pages/alerts/alert-logs-page/alert-logs-page.component').then(c => c.AlertLogsPageComponent),
    },
];

const organizationTemplateRoutes: Routes = [
    {
        path: 'askemo/:askemoTemplateId',
        title: 'str_askemoTemplate_title',
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'design',
            },
            {
                path: 'design',
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                    import('@pages/organization/templates/organization-templates-askemo-design-page/organization-templates-askemo-design-page.component').then(
                        c => c.OrganizationTemplatesAskemoDesignPageComponent
                    ),
            },
            {
                path: 'invitation',
                canDeactivate: [unsavedChangesGuard],
                loadComponent: () =>
                    import('@pages/organization/templates/organization-templates-askemo-invitations-page/organization-templates-askemo-invitations-page.component').then(
                        c => c.OrganizationTemplatesAskemoInvitationsPageComponent
                    ),
            },
        ],
    },
    {
        path: 'question/:questionTemplateId',
        title: 'str_questionTemplate_title',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
            import('@pages/organization/templates/organization-templates-question-template-page/organization-templates-question-template-page.component').then(
                c => c.OrganizationTemplatesQuestionTemplatePageComponent
            ),
    },
    {
        path: 'invitation/:invitationTemplateId/:type',
        title: 'str_invitationTemplate_title',
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
            import('@pages/organization/templates/organization-templates-invitation-template-page/organization-templates-invitation-template-page.component').then(
                c => c.OrganizationTemplatesInvitationTemplatePageComponent
            ),
    },
];
const organizationLibraryRouteWithoutLeftMenu: Route = {
    path: 'library/:organizationLibraryId',
    children: [
        {
            path: 'askemo/:askemoTemplateId',
            title: 'str_askemoTemplate_title',
            children: [
                {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: 'design',
                },
                {
                    path: 'design',
                    loadComponent: () =>
                        import('@pages/organization/library/organization-library-askemo-design-page/organization-library-askemo-design-page.component').then(
                            c => c.OrganizationLibraryAskemoDesignPageComponent
                        ),
                },
                {
                    path: 'invitation',
                    loadComponent: () =>
                        import('@pages/organization/library/organization-library-askemo-invitations-page/organization-library-askemo-invitations-page.component').then(
                            c => c.OrganizationLibraryAskemoInvitationsPageComponent
                        ),
                },
            ],
        },
        {
            path: 'question/:questionTemplateId',
            title: 'str_questionTemplate_title',
            loadComponent: () =>
                import('@pages/organization/library/organization-library-question-template/organization-library-question-template.component').then(
                    c => c.OrganizationLibraryQuestionTemplateComponent
                ),
        },
        {
            path: 'invitation/:invitationTemplateId/:type',
            title: 'str_invitationTemplate_title',
            loadComponent: () =>
                import('@pages/organization/library/organization-library-invitation-template/organization-library-invitation-template.component').then(
                    c => c.OrganizationLibraryInvitationTemplateComponent
                ),
        },
    ],
};
const organizationLibraryRoute: Route = {
    path: 'library/:organizationLibraryId/:tab',
    children: [
        {
            path: '',
            pathMatch: 'full',
            loadComponent: () => import('@pages/organization/library/organization-library-page/organization-library-page.component').then(c => c.OrganizationLibraryPageComponent),
        },
    ],
};

const twoFactorAuthRoute: Route = {
    path: '2fa',
    title: 'str_integrations_twoFactorAuthentication_title',
    loadComponent: () => import('@pages/organization/connectors/two-factor-authentication/two-factor-authentication.component').then(c => c.TwoFactorAuthenticationComponent),
};

const ssoAuthRoute: Route = {
    path: 'sso',
    canDeactivate: [unsavedChangesGuard],
    title: 'str_integrations_singleSignOn_title',
    loadComponent: () => import('@pages/organization/connectors/single-sign-on/single-sign-on.component').then(c => c.SingleSignOnComponent),
};

export const routes: Routes = [
    {
        path: 'unsubscribe/:sessionId',
        canDeactivate: [publicPagesGuard],
        loadComponent: () => import('@pages/askemos/unsubscribe-page/unsubscribe-page.component').then(c => c.UnsubscribePageComponent),
    },

    {
        path: 'test/:askemoId',
        canActivate: [publicPagesGuard],
        loadComponent: () => import('@pages/askemos/askemo-test-page/askemo-test-page.component').then(c => c.AskemoTestPageComponent),
    },
    {
        path: 'a/:urlInvitationHash',
        canActivate: [publicPagesGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('@pages/askemos/askemo-answer-url-page/askemo-answer-url-page.component').then(c => c.AskemoAnswerUrlPageComponent),
    },
    {
        path: 'answer/:sessionContactId',
        canActivate: [publicPagesGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('@pages/askemos/askemo-answer-page/askemo-answer-page.component').then(c => c.AskemoAnswerPageComponent),
    },
    {
        path: 'answer/:topicContactHash/t',
        canActivate: [publicPagesGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () => import('@pages/askemos/askemo-answer-topic-page/askemo-answer-topic-page.component').then(c => c.AskemoAnswerTopicPageComponent),
    },
    {
        path: 'dashboard',
        canActivate: [publicPagesGuard],
        children: [
            {
                path: ':dashboardId',
                data: { passwordProtected: false },
                loadComponent: () => import('@pages/analytics/shared-dashboard-page/shared-dashboard-page.component').then(c => c.SharedDashboardPageComponent),
            },
            {
                path: 'p/:dashboardId',
                data: { passwordProtected: true },
                loadComponent: () => import('@pages/analytics/shared-dashboard-page/shared-dashboard-page.component').then(c => c.SharedDashboardPageComponent),
            },
        ],
    },
    {
        path: 'public',
        canActivate: [publicPagesGuard],
        loadComponent: () => import('@shared/components/public-pages').then(c => c.PublicPagesComponent),
        children: [],
    },
    {
        path: 'acceptInvitation',
        loadComponent: () => import('@shared/components/accept-invitation/accept-invitation.component').then(c => c.AcceptInvitationComponent),
    },
    {
        path: 'login/:username',
        canActivate: [publicPagesGuard],
        loadComponent: () => import('@core/authentication/components/sso-login-page/sso-login-page.component').then(c => c.SsoLoginPageComponent),
    },
    {
        path: '',
        loadComponent: () => import('@shared/components/private-pages').then(c => c.PrivatePagesComponent),
        canActivate: [AuthenticationGuard, privatePagesGuard],
        children: [
            {
                path: '',
                loadComponent: () => import('./shared/components/sidenav-wrapper/sidenav-wrapper.component').then(c => c.SidenavWrapperComponent),
                children: [
                    {
                        path: '',
                        canActivate: [landingPageGuard],
                        loadComponent: () => import('./shared/components/public-pages/public-pages.component').then(c => c.PublicPagesComponent),
                        pathMatch: 'full',
                    },

                    {
                        path: 'user',
                        children: [
                            {
                                path: '',
                                redirectTo: 'profile',
                                pathMatch: 'full',
                            },
                            {
                                path: 'profile',
                                title: 'str_page_titleUserDetails',
                                canDeactivate: [unsavedChangesGuard],
                                loadComponent: () => import('./pages/user/components/user-details/user-details.component').then(c => c.UserDetailsComponent),
                            },
                        ],
                    },

                    {
                        path: 'organization',
                        children: [
                            {
                                path: '',
                                redirectTo: 'details',
                                pathMatch: 'full',
                            },
                            {
                                path: 'details',
                                title: 'str_common_details',
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'company',
                                        pathMatch: 'full',
                                    },
                                    {
                                        path: ':tab',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/organization/components/organization-details/organization-details.component').then(c => c.OrganizationDetailsComponent),
                                    },
                                ],
                            },
                            {
                                path: 'users',
                                title: 'str_page_titleUsers',
                                loadComponent: () => import('@pages/user/components/user-list/user-list.component').then(c => c.UserListComponent),
                            },
                            {
                                path: 'roles',
                                title: 'str_page_titleRoles',
                                canActivate: [createLegacyModuleScopedRedirectGuard('security/roles')],
                                loadComponent: () => import('@pages/roles/components/roles/roles.component').then(c => c.RolesComponent),
                            },
                            {
                                path: 'invoices',
                                title: 'str_page_titleInvoices',
                                loadComponent: () => import('@pages/invoices/components/invoices/invoices.component').then(c => c.InvoicesComponent),
                            },
                            {
                                path: 'projects',
                                title: 'str_page_titleProjects',
                                loadComponent: () =>
                                    import('@pages/projects/components/organization-projects-page/organization-projects-page.component').then(c => c.ProjectsListComponent),
                            },
                            {
                                path: 'topics',
                                title: 'str_page_titleTopics',
                                canActivate: [createLegacyModuleScopedRedirectGuard('topics')],
                                loadComponent: () => import('@pages/topics/organization-topics-page.component').then(c => c.OrganizationTopicsPageComponent),
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'questions',
                                        pathMatch: 'full',
                                    },
                                    {
                                        path: 'questions',
                                        loadComponent: () => import('@pages/topics/question-categories/question-categories.component').then(c => c.QuestionCategoriesListComponent),
                                    },
                                    {
                                        path: 'askemo',
                                        loadComponent: () => import('@pages/topics/askemo-topics/askemo-topics-page.component').then(c => c.AskemoTopicsPageComponent),
                                    },
                                ],
                            },
                            {
                                path: 'connectors',
                                title: 'str_common_connectors',
                                canActivate: [createLegacyModuleScopedRedirectGuard('connectors')],
                                children: [
                                    {
                                        path: '',
                                        loadComponent: () =>
                                            import('@pages/organization/connectors/connectors-page/connectors-page.component').then(c => c.ConnectorsPageComponent),
                                        children: [
                                            {
                                                path: '',
                                                redirectTo: 'integrations',
                                                pathMatch: 'full',
                                            },
                                            {
                                                path: 'integrations',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/integrations-list/integrations-list.component').then(
                                                                c => c.IntegrationsListComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'apiKeys',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/api-keys/api-keys-list/api-keys-list.component').then(
                                                                c => c.ApiKeysListComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: '2fa',
                                                redirectTo: '/organization/authentication/2fa',
                                                pathMatch: 'full',
                                            },
                                            {
                                                path: 'sso',
                                                redirectTo: '/organization/authentication/sso',
                                                pathMatch: 'full',
                                            },
                                        ],
                                    },
                                    {
                                        path: 'integrations',
                                        children: [
                                            {
                                                path: 'afas',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/afas/afas-setup-page/afas-setup-page.component').then(
                                                                c => c.AfasSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/afas/afas-mapping-page/afas-mapping-page.component').then(
                                                                c => c.AfasMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'bundeling',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/bundeling/bundeling-setup-page/bundeling-setup-page.component').then(
                                                                c => c.BundelingSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/bundeling/bundeling-mapping-page/bundeling-mapping-page.component').then(
                                                                c => c.BundelingMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'nmbrs',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/nmbrs/nmbrs-setup-page.component').then(
                                                                c => c.NmbrsSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/nmbrs/nmbrs-mapping-page.component').then(
                                                                c => c.NmbrsMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'sdb',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/sdb/sdb-setup-page.component').then(c => c.SdbSetupPageComponent),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/sdb/sdb-mapping-page.component').then(
                                                                c => c.SdbMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'microsoft',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/microsoft/microsoft-setup-page/microsoft-setup-page.component').then(
                                                                c => c.MicrosoftSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/microsoft/microsoft-mapping-page/microsoft-mapping-page.component').then(
                                                                c => c.MicrosoftMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'adaptivecard',
                                                canDeactivate: [unsavedChangesGuard],
                                                loadComponent: () =>
                                                    import('@pages/organization/connectors/integrations/adaptive-card/adaptive-card-setup-page/adaptive-card-setup-page.component').then(
                                                        c => c.AdaptiveCardSetupPageComponent
                                                    ),
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'authentication',
                                title: 'str_common_authentication',
                                loadComponent: () => import('@pages/organization/connectors/connectors-page/connectors-page.component').then(c => c.ConnectorsPageComponent),
                                children: [
                                    {
                                        path: '',
                                        redirectTo: '2fa',
                                        pathMatch: 'full',
                                    },
                                    twoFactorAuthRoute,
                                    ssoAuthRoute,
                                ],
                            },
                            {
                                path: 'templates',
                                title: 'str_page_templates',
                                canActivate: [
                                    createLegacyModuleScopedRedirectGuard('templates', remainder =>
                                        remainder[0] === 'organization-templates' ? ['module', ...remainder.slice(1)] : remainder
                                    ),
                                ],
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'library',
                                        pathMatch: 'full',
                                    },
                                    organizationLibraryRoute,
                                    {
                                        path: 'organization-templates',
                                        loadComponent: () => import('@pages/organization/organization-templates/templates-page.component').then(c => c.TemplatesPageComponent),
                                        children: [
                                            {
                                                path: '',
                                                redirectTo: 'askemo',
                                                pathMatch: 'full',
                                            },
                                            {
                                                path: ':tab',
                                                loadComponent: () =>
                                                    import('@pages/organization/templates/organization-templates-page/organization-templates-page.component').then(
                                                        c => c.OrganizationTemplatesPageComponent
                                                    ),
                                            },
                                        ],
                                    },
                                    {
                                        path: 'library',
                                        loadComponent: () => import('@pages/organization/organization-templates/templates-page.component').then(c => c.TemplatesPageComponent),
                                        children: [
                                            {
                                                path: '',
                                                pathMatch: 'full',
                                                loadComponent: () =>
                                                    import('@pages/organization/library/organization-library-list-page/organization-library-list-page.component').then(
                                                        c => c.OrganizationLibraryListPageComponent
                                                    ),
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'connectors',
                                title: 'str_common_connectors',
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'integrations',
                                        pathMatch: 'full',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'departments',
                        title: 'str_page_titleDepartments',
                        canActivate: [createLegacyModuleScopedRedirectGuard('security/departments')],
                        loadComponent: () => import('@pages/department/components/departments/departments.component').then(c => c.DepartmentsComponent),
                    },
                    {
                        path: 'mailsettings',
                        title: 'str_page_titleMailSettings',
                        canActivate: [createLegacyModuleScopedRedirectGuard('mailsettings')],
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                canDeactivate: [unsavedChangesGuard],
                                loadComponent: () => import('@pages/mail-settings/components/mail-settings/mail-settings.component').then(c => c.MailSettingsComponent),
                            },
                            {
                                path: 'url',
                                canDeactivate: [unsavedChangesGuard],
                                loadComponent: () =>
                                    import('@pages/mail-settings/components/invitation-url-settings/invitation-url-settings.component').then(c => c.InvitationUrlSettingsComponent),
                            },
                        ],
                    },
                ],
            },
            {
                path: 'alerts',
                canActivate: [createLegacyProjectScopedRedirectGuard('alerts')],
                children: alertsRoutes,
            },
            {
                path: 'contacts',
                canActivate: [createLegacyModuleScopedRedirectGuard('contacts'), joinContactImportHub],
                canDeactivate: [leaveContactImportHub],
                children: contactsRoutes,
            },
            {
                path: 'askemos',
                canActivate: [legacyAskemoRedirectGuard],
                children: askemosRoutes,
            },
            {
                path: 'analytics',
                title: 'str_page_analytics',
                canActivate: [createLegacyModuleScopedRedirectGuard('results/analytics')],
                children: [],
            },
            {
                path: 'results',
                canActivate: [createLegacyModuleScopedRedirectGuard('results/table')],
                children: [],
            },
            {
                path: 'admin',
                canActivate: [AdminGuard],
                children: [
                    {
                        path: '',
                        redirectTo: 'organizations',
                        pathMatch: 'full',
                    },
                    {
                        path: 'organizations',
                        title: 'str_page_titleClients',
                        canDeactivate: [unsavedChangesGuard],
                        loadComponent: () =>
                            import('./pages/admin/components/client-organizations/admin-client-organizations.component').then(c => c.AdminClientOrganizationsComponent),
                    },
                    {
                        path: 'users',
                        title: 'str_page_titleUsers',
                        loadComponent: () => import('./pages/admin/components/users/admin-users.component').then(c => c.AdminUsersComponent),
                    },
                    {
                        path: 'datasets',
                        title: 'str_datasets_title',
                        loadComponent: () => import('./pages/admin/components/datasets/datasets-page.component').then(c => c.DatasetsPageComponent),
                    },
                    {
                        path: 'messages',
                        title: 'str_systemMessages_navTitle',
                        loadComponent: () => import('./pages/admin/messages/system-messages-page.component').then(c => c.SystemMessagesPageComponent),
                    },
                    {
                        path: 'modules',
                        title: 'str_moduleSettings_title',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                loadComponent: () => import('./pages/admin/modules/system-modules.page').then(c => c.SystemModulesPageComponent),
                            },
                            {
                                path: ':module',
                                canActivate: [systemModuleSlugGuard],
                                loadComponent: () => import('./pages/admin/modules/system-module-settings/system-module-settings').then(c => c.SystemModuleSettingsComponent),
                                children: [
                                    {
                                        path: '',
                                        pathMatch: 'full',
                                        redirectTo: 'system-agents',
                                    },
                                    {
                                        path: 'system-agents',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/admin/modules/system-module-settings-system-agents/system-module-settings-system-agents').then(
                                                c => c.SystemModuleSettingsSystemAgentsComponent
                                            ),
                                    },
                                    {
                                        path: 'connectors',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/admin/modules/system-module-settings-connectors/system-module-settings-connectors').then(
                                                c => c.SystemModuleSettingsConnectorsComponent
                                            ),
                                    },
                                    {
                                        path: 'libraries',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/admin/modules/system-module-settings-libraries/system-module-settings-libraries').then(
                                                c => c.SystemModuleSettingsLibrariesComponent
                                            ),
                                    },
                                    {
                                        path: 'reports',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/admin/modules/system-module-settings-reports/system-module-settings-reports').then(
                                                c => c.SystemModuleSettingsReportsComponent
                                            ),
                                        children: [
                                            {
                                                path: '',
                                                pathMatch: 'full',
                                                loadComponent: () =>
                                                    import('./pages/admin/modules/system-module-settings-reports/system-prompt-detail/system-prompt-detail').then(
                                                        c => c.SystemPromptDetailComponent
                                                    ),
                                            },
                                            {
                                                path: 'create',
                                                loadComponent: () =>
                                                    import('./pages/admin/modules/system-module-settings-reports/create-report-type-detail/create-report-type-detail').then(
                                                        c => c.CreateReportTypeDetailComponent
                                                    ),
                                            },
                                            {
                                                path: ':reportTypeId',
                                                loadComponent: () =>
                                                    import('./pages/admin/modules/system-module-settings-reports/report-type-detail/report-type-detail').then(
                                                        c => c.ReportTypeDetailComponent
                                                    ),
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'logs',
                        title: 'str_admin_auditLogsTitle',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                loadComponent: () => import('./pages/admin/components/audit-logs/audit-logs.component').then(c => c.AuditLogsComponent),
                            },
                            {
                                path: ':auditLogId',
                                loadComponent: () =>
                                    import('./pages/admin/components/audit-logs/audit-log-details/audit-log-details.component').then(c => c.AuditLogDetailsComponent),
                            },
                        ],
                    },
                    {
                        path: 'embeddable-dashboards',
                        title: 'str_admin_embeddableDashboardsTitle',
                        loadComponent: () =>
                            import('./pages/admin/components/admin-embeddable-dashboards/admin-embeddable-dashboards-page.component').then(
                                c => c.AdminEmbeddableDashboardsPageComponent
                            ),
                    },
                    {
                        path: 'library',
                        title: 'str_library_pageTitle',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                loadComponent: () => import('./pages/admin/library/library-list-page/library-list-page.component').then(c => c.LibraryListPageComponent),
                            },
                            {
                                path: ':libraryId',
                                children: [
                                    {
                                        path: '',
                                        pathMatch: 'full',
                                        redirectTo: 'askemo',
                                    },
                                    {
                                        path: ':tab',
                                        loadComponent: () => import('./pages/admin/library/library-page/library-page.component').then(c => c.LibraryPageComponent),
                                    },
                                    {
                                        path: 'askemo/:askemoTemplateId',
                                        title: 'str_askemoTemplate_title',
                                        children: [
                                            {
                                                path: '',
                                                pathMatch: 'full',
                                                redirectTo: 'design',
                                            },
                                            {
                                                path: 'design',
                                                canDeactivate: [unsavedChangesGuard],
                                                loadComponent: () =>
                                                    import('./pages/admin/library/askemo-template/library-askemo-template-design-page/library-askemo-template-design-page.component').then(
                                                        c => c.LibraryAskemoTemplateDesignPageComponent
                                                    ),
                                            },
                                            {
                                                path: 'invitation',
                                                canDeactivate: [unsavedChangesGuard],
                                                loadComponent: () =>
                                                    import('./pages/admin/library/askemo-template/library-askemo-template-invitation-page/library-askemo-template-invitation-page.component').then(
                                                        c => c.LibraryAskemoTemplateInvitationPageComponent
                                                    ),
                                            },
                                        ],
                                    },
                                    {
                                        path: 'question/:questionTemplateId',
                                        title: 'str_questionTemplate_title',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/admin/library/library-question-template/library-question-template.component').then(
                                                c => c.LibraryQuestionTemplateComponent
                                            ),
                                    },
                                    {
                                        path: 'invitation/:invitationTemplateId/:type',
                                        title: 'str_invitationTemplate_title',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/admin/library/library-invitation-template/library-invitation-template.component').then(
                                                c => c.LibraryInvitationTemplateComponent
                                            ),
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                path: 'organization',
                children: [
                    {
                        path: 'templates',
                        children: [
                            {
                                path: 'organization-templates',
                                children: organizationTemplateRoutes,
                            },
                            organizationLibraryRouteWithoutLeftMenu,
                        ],
                    },
                ],
            },
            {
                path: 'playground',
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        loadComponent: () => import('@pages/playground/playground.component').then(c => c.PlaygroundComponent),
                    },
                    {
                        path: 'forms',
                        loadComponent: () => import('@pages/playground/forms/playground-forms.component').then(c => c.PlaygroundFormsComponent),
                    },
                    // TEMP: remove after ASKEMO-2668 modal redesign QA
                    {
                        path: 'dialogs',
                        loadComponent: () => import('@pages/playground/dialogs/playground-dialogs.component').then(c => c.PlaygroundDialogsComponent),
                    },
                ],
            },
            {
                path: ':module',
                canActivate: [moduleContextGuard],
                children: [
                    {
                        path: '',
                        canActivate: [landingPageGuard],
                        loadComponent: () => import('./shared/components/public-pages/public-pages.component').then(c => c.PublicPagesComponent),
                        pathMatch: 'full',
                    },
                    {
                        path: '',
                        loadComponent: () => import('./shared/components/sidenav-wrapper/sidenav-wrapper.component').then(c => c.SidenavWrapperComponent),
                        children: [
                            {
                                path: 'departments',
                                redirectTo: 'security/departments',
                                pathMatch: 'full',
                            },
                            {
                                path: 'roles',
                                redirectTo: 'security/roles',
                                pathMatch: 'full',
                            },
                            {
                                path: 'security',
                                title: 'str_common_security',
                                loadComponent: () => import('@pages/module/security/module-security-page.component').then(c => c.ModuleSecurityPageComponent),
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'departments',
                                        pathMatch: 'full',
                                    },
                                    {
                                        path: 'departments',
                                        title: 'str_page_titleDepartments',
                                        loadComponent: () => import('@pages/department/components/departments/departments.component').then(c => c.DepartmentsComponent),
                                    },
                                    {
                                        path: 'roles',
                                        title: 'str_page_titleRoles',
                                        loadComponent: () => import('@pages/roles/components/roles/roles.component').then(c => c.RolesComponent),
                                    },
                                ],
                            },
                            {
                                path: 'details',
                                title: 'str_common_details',
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'module',
                                        pathMatch: 'full',
                                    },
                                    {
                                        path: ':tab',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('./pages/organization/components/organization-details/organization-details.component').then(c => c.OrganizationDetailsComponent),
                                    },
                                ],
                            },
                            {
                                path: 'topics',
                                title: 'str_page_titleTopics',
                                loadComponent: () => import('@pages/topics/organization-topics-page.component').then(c => c.OrganizationTopicsPageComponent),
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'questions',
                                        pathMatch: 'full',
                                    },
                                    {
                                        path: 'questions',
                                        loadComponent: () => import('@pages/topics/question-categories/question-categories.component').then(c => c.QuestionCategoriesListComponent),
                                    },
                                    {
                                        path: 'askemo',
                                        loadComponent: () => import('@pages/topics/askemo-topics/askemo-topics-page.component').then(c => c.AskemoTopicsPageComponent),
                                    },
                                ],
                            },
                            {
                                path: 'templates',
                                title: 'str_page_templates',
                                children: [
                                    {
                                        path: '',
                                        loadComponent: () => import('@pages/organization/organization-templates/templates-page.component').then(c => c.TemplatesPageComponent),
                                        children: [
                                            {
                                                path: '',
                                                redirectTo: 'library',
                                                pathMatch: 'full',
                                            },
                                            {
                                                path: 'library',
                                                loadComponent: () =>
                                                    import('@pages/organization/library/organization-library-list-page/organization-library-list-page.component').then(
                                                        c => c.OrganizationLibraryListPageComponent
                                                    ),
                                            },
                                            {
                                                path: 'module',
                                                children: [
                                                    {
                                                        path: '',
                                                        redirectTo: 'askemo',
                                                        pathMatch: 'full',
                                                    },
                                                    {
                                                        path: ':tab',
                                                        loadComponent: () =>
                                                            import('@pages/organization/templates/organization-templates-page/organization-templates-page.component').then(
                                                                c => c.OrganizationTemplatesPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    organizationLibraryRoute,
                                ],
                            },
                            {
                                path: 'connectors',
                                title: 'str_common_connectors',
                                children: [
                                    {
                                        path: '',
                                        loadComponent: () =>
                                            import('@pages/organization/connectors/connectors-page/connectors-page.component').then(c => c.ConnectorsPageComponent),
                                        children: [
                                            {
                                                path: '',
                                                redirectTo: 'integrations',
                                                pathMatch: 'full',
                                            },
                                            {
                                                path: 'integrations',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/integrations-list/integrations-list.component').then(
                                                                c => c.IntegrationsListComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'apiKeys',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/api-keys/api-keys-list/api-keys-list.component').then(
                                                                c => c.ApiKeysListComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: 'integrations',
                                        children: [
                                            {
                                                path: 'afas',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/afas/afas-setup-page/afas-setup-page.component').then(
                                                                c => c.AfasSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/afas/afas-mapping-page/afas-mapping-page.component').then(
                                                                c => c.AfasMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'bundeling',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/bundeling/bundeling-setup-page/bundeling-setup-page.component').then(
                                                                c => c.BundelingSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/bundeling/bundeling-mapping-page/bundeling-mapping-page.component').then(
                                                                c => c.BundelingMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'nmbrs',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/nmbrs/nmbrs-setup-page.component').then(
                                                                c => c.NmbrsSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/nmbrs/nmbrs-mapping-page.component').then(
                                                                c => c.NmbrsMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'sdb',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/sdb/sdb-setup-page.component').then(c => c.SdbSetupPageComponent),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/sdb/sdb-mapping-page.component').then(
                                                                c => c.SdbMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'microsoft',
                                                children: [
                                                    {
                                                        path: '',
                                                        pathMatch: 'full',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/microsoft/microsoft-setup-page/microsoft-setup-page.component').then(
                                                                c => c.MicrosoftSetupPageComponent
                                                            ),
                                                    },
                                                    {
                                                        path: 'mapping',
                                                        canDeactivate: [unsavedChangesGuard],
                                                        loadComponent: () =>
                                                            import('@pages/organization/connectors/integrations/microsoft/microsoft-mapping-page/microsoft-mapping-page.component').then(
                                                                c => c.MicrosoftMappingPageComponent
                                                            ),
                                                    },
                                                ],
                                            },
                                            {
                                                path: 'adaptivecard',
                                                canDeactivate: [unsavedChangesGuard],
                                                loadComponent: () =>
                                                    import('@pages/organization/connectors/integrations/adaptive-card/adaptive-card-setup-page/adaptive-card-setup-page.component').then(
                                                        c => c.AdaptiveCardSetupPageComponent
                                                    ),
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: 'mailsettings',
                                title: 'str_page_titleMailSettings',
                                children: [
                                    {
                                        path: '',
                                        pathMatch: 'full',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () => import('@pages/mail-settings/components/mail-settings/mail-settings.component').then(c => c.MailSettingsComponent),
                                    },
                                    {
                                        path: 'url',
                                        canDeactivate: [unsavedChangesGuard],
                                        loadComponent: () =>
                                            import('@pages/mail-settings/components/invitation-url-settings/invitation-url-settings.component').then(
                                                c => c.InvitationUrlSettingsComponent
                                            ),
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'home',
                        title: 'str_common_home',
                        loadComponent: () => import('@pages/module/home/module-home-page.component').then(c => c.ModuleHomePageComponent),
                    },
                    {
                        path: 'contacts',
                        canActivate: [joinContactImportHub],
                        canDeactivate: [leaveContactImportHub],
                        children: contactsRoutes,
                    },
                    // Results: Analytics and Actions tabs, and the results list under results/table.
                    ...resultsHubRoutes,
                    {
                        path: 'analytics',
                        pathMatch: 'full',
                        redirectTo: 'results/analytics',
                    },
                    {
                        path: 'actions',
                        pathMatch: 'full',
                        redirectTo: 'results/actions',
                    },
                    // Old project-level Results, Actions and Analytics routes now open the module-level ones.
                    // Redirects are applied before guards, so the project isn't selected or checked. Must stay after 'results'.
                    {
                        path: ':project/results',
                        redirectTo: 'results/table',
                    },
                    {
                        path: ':project/actions',
                        redirectTo: 'results/actions',
                    },
                    {
                        path: ':project/analytics',
                        redirectTo: 'results/analytics',
                    },
                    {
                        path: 'templates',
                        children: [
                            {
                                path: 'module',
                                children: organizationTemplateRoutes,
                            },
                            organizationLibraryRouteWithoutLeftMenu,
                        ],
                    },
                    {
                        path: ':project',
                        canActivate: [projectContextGuard],
                        canDeactivate: [projectContextDeactivateGuard],
                        children: [
                            {
                                path: '',
                                canActivate: [landingPageGuard],
                                loadComponent: () => import('./shared/components/public-pages/public-pages.component').then(c => c.PublicPagesComponent),
                                pathMatch: 'full',
                            },
                            {
                                path: 'askemos',
                                children: askemosRoutes,
                            },
                            {
                                path: 'alerts',
                                children: alertsRoutes,
                            },
                        ],
                    },
                ],
            },
        ],
    },

    {
        path: '**',
        redirectTo: '',
    },
];
